import React, { useState, useEffect } from 'react';
import { X, Star, CheckCircle, DollarSign, User, Loader2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '@/contexts/ThemeContext';

// Command Deck dark theme tokens (visual only)
const FONT_DISPLAY = "'Poppins', 'Space Grotesk', sans-serif";
const FONT_BODY = "'Work Sans', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

const inputBase = {
  width: '100%',
  padding: '10px 13px',
  border: '1px solid rgba(255,255,255,.14)',
  borderRadius: 11,
  background: 'rgba(255,255,255,.04)',
  color: '#fff',
  fontFamily: FONT_BODY,
  fontSize: 13.5,
  outline: 'none',
};
const inputFocus = (e) => { e.target.style.boxShadow = '0 0 0 3px rgba(40,192,208,.12)'; e.target.style.borderColor = 'rgba(40,192,208,.5)'; };
const inputBlur = (e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(255,255,255,.14)'; };

const CompleteJobModal = ({ isOpen, onClose, job, hiredCrew, userEmail, userId, onSuccess, onJobCompleted }) => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');

  // Extra payment state
  const [showExtraPayment, setShowExtraPayment] = useState(false);
  const [extraAmount, setExtraAmount] = useState('');
  const [extraReason, setExtraReason] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [loadingMethods, setLoadingMethods] = useState(false);

  useEffect(() => {
    if (showExtraPayment && paymentMethods.length === 0) {
      fetchPaymentMethods();
    }
  }, [showExtraPayment]);

  useEffect(() => {
    if (isOpen) {
      setRating(0);
      setReview('');
      setShowExtraPayment(false);
      setExtraAmount('');
      setExtraReason('');
      setSelectedPaymentMethod('');
    }
  }, [isOpen]);

  const fetchPaymentMethods = async () => {
    const email = userEmail || JSON.parse(localStorage.getItem('shootzu_user') || '{}').email;
    if (!email) return;
    setLoadingMethods(true);
    try {
      const token = localStorage.getItem('shootzu_token');
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/methods?user_email=${encodeURIComponent(email)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const methods = res.data.payment_methods || [];
      setPaymentMethods(methods);
      if (methods.length > 0) setSelectedPaymentMethod(methods[0].id);
    } catch (e) {
      console.error('Failed to load payment methods:', e);
    } finally {
      setLoadingMethods(false);
    }
  };

  if (!isOpen) return null;

  const crew = hiredCrew || job?.hiredCrew;
  const crewName = crew?.full_name || crew?.name || crew?.hired_name || 'Crew Member';
  const crewPayout = job?.crew_payout || job?.crewPayout || (crew?.subtotal ? crew.subtotal * 0.98 : null) || (job?.total_amount ? job.total_amount * 0.98 : null) || 0;
  const extraAmountNum = parseFloat(extraAmount) || 0;
  const platformFee = extraAmountNum * 0.05;
  const totalExtraCharge = extraAmountNum + platformFee;

  const formatCurrency = (val) => `$${(parseFloat(val) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleComplete = async () => {
    if (rating === 0) { toast.error('Please provide a rating'); return; }
    if (!review.trim()) { toast.error('Please provide a review'); return; }
    if (showExtraPayment && extraAmountNum > 0) {
      if (!extraReason.trim()) { toast.error('Please provide a reason for the extra payment'); return; }
      if (!selectedPaymentMethod) { toast.error('Please select a payment method'); return; }
    }

    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
      const token = localStorage.getItem('shootzu_token');
      if (!token) { toast.error('Please log in'); return; }

      const reviewData = {
        job_id: job.id,
        reviewer_id: userId || user.id,
        reviewer_name: user.full_name || user.fullName || user.email,
        reviewer_email: userEmail || user.email,
        rating,
        comment: review.trim(),
        extra_payment: (showExtraPayment && extraAmountNum > 0) ? {
          amount: extraAmountNum,
          reason: extraReason.trim(),
          payment_method_id: selectedPaymentMethod,
        } : null,
        crew_member: job.crewMember ? {
          user_id: job.crewMember.hired_user_id || crew?.userId || crew?.hired_user_id,
          name: job.crewMember.hired_name || crew?.name || crew?.hired_name,
          email: job.crewMember.hired_email || crew?.email || crew?.hired_email,
          role: job.crewMember.role || crew?.role,
          subtotal: job.crewMember.subtotal || crew?.subtotal,
          crew_payout: crewPayout,
        } : null,
      };

      // Use per-crew endpoint for advanced jobs, standard for simple
      const endpoint = (job.isAdvanced && job.crewMember)
        ? `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/release-payment-crew`
        : `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/release-payment`;

      const response = await axios.post(endpoint, reviewData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Review submitted and payment release initiated!');
        onSuccess && onSuccess();
        onJobCompleted && onJobCompleted(job.id);
        onClose();
      }
    } catch (error) {
      console.error('Complete job error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ padding: 20, fontFamily: FONT_BODY }}
    >
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{ background: 'rgba(8,11,20,.66)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)' }}
      />

      <div
        className="relative w-full max-w-lg flex flex-col"
        style={{
          background: '#12162A',
          border: '1px solid rgba(255,255,255,.12)',
          borderRadius: 22,
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
          overflow: 'hidden',
          maxHeight: '90vh',
        }}
      >
        {/* Header band — green success context */}
        <div
          className="flex-shrink-0"
          style={{
            position: 'relative',
            padding: '22px 26px',
            background: 'linear-gradient(120deg,#15803D,#0E7490)',
            borderRadius: '22px 22px 0 0',
          }}
        >
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 34,
              height: 34,
              borderRadius: 10,
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.18)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 10,
            }}
          >
            <X className="w-[18px] h-[18px]" />
          </button>
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8" style={{ color: '#fff' }} />
            <div>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, color: '#fff', margin: 0, letterSpacing: '-.4px' }}>Complete Job</h2>
              <p style={{ color: 'rgba(255,255,255,.85)', fontSize: 13, margin: 0 }}>Release payment to crew member</p>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Job Info */}
          <div style={{ padding: 16, borderRadius: 14, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)' }}>
            <h3 style={{ fontWeight: 700, marginTop: 0, marginBottom: 12, color: '#fff', fontFamily: FONT_DISPLAY }}>{job?.title || job?.job_title}</h3>
            <div className="space-y-2" style={{ fontSize: 13 }}>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" style={{ color: '#67C6B4' }} />
                <span style={{ color: 'rgba(255,255,255,.7)' }}>Hired: {crewName}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" style={{ color: '#67C6B4' }} />
                <span style={{ color: 'rgba(255,255,255,.7)' }}>Crew Payout: {formatCurrency(crewPayout)}</span>
              </div>
            </div>
          </div>

          {/* Rating */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#fff' }}>
              Rate {crewName}'s Performance *
            </label>
            <div className="flex gap-2 justify-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} type="button" onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <Star
                    className="w-10 h-10"
                    style={{
                      color: star <= (hoverRating || rating) ? '#FCD34D' : 'rgba(255,255,255,.18)',
                      fill: star <= (hoverRating || rating) ? '#FCD34D' : 'transparent',
                    }}
                  />
                </button>
              ))}
            </div>
            <p style={{ textAlign: 'center', marginTop: 8, fontSize: 13, color: 'rgba(255,255,255,.5)' }}>
              {rating === 0 ? 'Click to rate' : `${rating} star${rating > 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Review */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#fff' }}>Write a Review *</label>
            <textarea value={review} onChange={(e) => setReview(e.target.value)}
              placeholder="Share your experience working with this crew member..."
              rows={4}
              style={{ ...inputBase, minHeight: 100, resize: 'vertical', lineHeight: 1.5 }}
              onFocus={inputFocus} onBlur={inputBlur} />
          </div>

          {/* Additional Payment */}
          <div style={{ padding: 16, borderRadius: 14, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
              <div className="flex items-center gap-2">
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Additional Payment</span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.5)' }}>(Optional)</span>
              </div>
              <button onClick={() => setShowExtraPayment(!showExtraPayment)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 9,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: showExtraPayment ? 'rgba(40,192,208,.16)' : 'rgba(255,255,255,.05)',
                  border: showExtraPayment ? '1px solid rgba(40,192,208,.4)' : '1px solid rgba(255,255,255,.14)',
                  color: showExtraPayment ? '#28C0D0' : 'rgba(255,255,255,.75)',
                }}>
                {showExtraPayment ? 'Remove' : '+ Add'}
              </button>
            </div>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', margin: 0 }}>
              Add payment for parking, overtime, or other agreed expenses
            </p>

            {showExtraPayment && (
              <div className="mt-4 space-y-3">
                {/* Quick amount chips */}
                <div className="flex gap-2">
                  {[25, 50, 100].map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setExtraAmount(String(chip))}
                      style={{
                        flex: 1,
                        padding: 8,
                        borderRadius: 9,
                        background: String(chip) === extraAmount ? 'rgba(40,192,208,.16)' : 'rgba(255,255,255,.05)',
                        border: String(chip) === extraAmount ? '1px solid rgba(40,192,208,.5)' : '1px solid rgba(255,255,255,.14)',
                        color: String(chip) === extraAmount ? '#28C0D0' : 'rgba(255,255,255,.75)',
                        fontFamily: FONT_BODY,
                        fontWeight: 600,
                        fontSize: 12.5,
                        cursor: 'pointer',
                      }}
                    >
                      +${chip}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4, color: 'rgba(255,255,255,.6)' }}>Amount ($) *</label>
                    <input type="number" value={extraAmount} onChange={(e) => setExtraAmount(e.target.value)}
                      placeholder="120" min="0" step="0.01"
                      style={{ ...inputBase, fontSize: 13 }} onFocus={inputFocus} onBlur={inputBlur} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4, color: 'rgba(255,255,255,.6)' }}>Reason *</label>
                    <input type="text" value={extraReason} onChange={(e) => setExtraReason(e.target.value)}
                      placeholder="Parking"
                      style={{ ...inputBase, fontSize: 13 }} onFocus={inputFocus} onBlur={inputBlur} />
                  </div>
                </div>

                {extraAmountNum > 0 && (
                  <div style={{ padding: 13, borderRadius: 13, background: 'rgba(103,198,180,.07)', border: '1px solid rgba(103,198,180,.22)' }}>
                    <div style={{ fontSize: 13 }} className="space-y-1">
                      <div className="flex justify-between">
                        <span style={{ color: 'rgba(255,255,255,.65)' }}>Extra Payment:</span>
                        <span style={{ fontWeight: 600, color: '#fff' }}>{formatCurrency(extraAmountNum)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'rgba(255,255,255,.65)' }}>Platform Fee (5%):</span>
                        <span style={{ fontWeight: 600, color: '#fff' }}>{formatCurrency(platformFee)}</span>
                      </div>
                      <div className="flex justify-between" style={{ borderTop: '1px solid rgba(103,198,180,.2)', paddingTop: 4, fontSize: 15, fontWeight: 700 }}>
                        <span style={{ color: '#fff' }}>Total Payable:</span>
                        <span style={{ color: '#67C6B4', fontFamily: FONT_DISPLAY, fontWeight: 800 }}>{formatCurrency(totalExtraCharge)}</span>
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(103,198,180,.2)' }}>
                      <p style={{ fontSize: 11, fontWeight: 600, marginTop: 0, marginBottom: 8, color: 'rgba(255,255,255,.7)' }}>Payment Method:</p>
                      {loadingMethods ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" style={{ color: '#28C0D0' }} />
                      ) : paymentMethods.length > 0 ? (
                        <div className="space-y-1.5">
                          {paymentMethods.map((method) => (
                            <label key={method.id} className="flex items-center gap-2"
                              style={{
                                padding: 8,
                                borderRadius: 10,
                                cursor: 'pointer',
                                border: selectedPaymentMethod === method.id ? '1px solid rgba(40,192,208,.5)' : '1px solid rgba(255,255,255,.14)',
                                background: selectedPaymentMethod === method.id ? 'rgba(40,192,208,.1)' : 'rgba(255,255,255,.04)',
                              }}>
                              <input type="radio" name="payment_method" value={method.id}
                                checked={selectedPaymentMethod === method.id}
                                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                style={{ width: 16, height: 16, accentColor: '#28C0D0' }} />
                              <CreditCard className="w-4 h-4" style={{ color: 'rgba(255,255,255,.5)' }} />
                              <span style={{ fontSize: 13, color: '#fff' }}>
                                {method.brand?.toUpperCase()} •••• {method.last4}
                              </span>
                              <span style={{ fontSize: 11, marginLeft: 'auto', color: 'rgba(255,255,255,.4)', fontFamily: FONT_MONO }}>
                                {method.exp_month}/{method.exp_year}
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: 11, color: '#F87171' }}>No saved payment methods. Add a card in your profile first.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment Release Notice */}
          <div style={{ padding: 16, borderRadius: 14, border: '1px solid rgba(103,198,180,.22)', background: 'rgba(103,198,180,.07)' }}>
            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5" style={{ color: '#67C6B4', marginTop: 2 }} />
              <div>
                <p style={{ fontWeight: 600, color: '#67C6B4', margin: 0 }}>Payment Release</p>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', margin: 0 }}>
                  By completing this job, {formatCurrency(crewPayout)}{extraAmountNum > 0 ? ` + ${formatCurrency(extraAmountNum)} extra` : ''} will be released to {crewName}.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="p-6 pt-0 flex gap-3 flex-shrink-0">
          <button onClick={onClose} disabled={loading}
            style={{
              flex: 1,
              height: 48,
              borderRadius: 12,
              background: 'transparent',
              border: '1px solid rgba(255,255,255,.2)',
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}>
            Cancel
          </button>
          <button onClick={handleComplete} disabled={loading || rating === 0 || !review.trim()}
            style={{
              flex: 1.6,
              height: 48,
              borderRadius: 12,
              background: '#15803D',
              border: 'none',
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (loading || rating === 0 || !review.trim()) ? 'not-allowed' : 'pointer',
              opacity: (loading || rating === 0 || !review.trim()) ? 0.6 : 1,
            }}>
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</> : 'Complete & Release Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompleteJobModal;
